import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { Container, Section } from 'components/GridV2';
import VideoLoaderResponsive from 'components/VideoLoaderResponsive';
import { Play } from 'components/icons/Play';
import { Accordion } from 'components/rebrand/Accordion';
import CustomPortableText from 'components/rebrand/CustomPortableText';

import { ISanityImage } from 'typescriptDefs';

import { P } from './rebrand/Typography';

type Props = {
  altText?: string | null;
  className?: string;
  description?: PortableTextBlock | PortableTextBlock[] | null;
  heading?: PortableTextBlock | PortableTextBlock[] | null;
  noOverlay?: boolean;
  thumbnailDesktop: ISanityImage | null;
  thumbnailMobile: ISanityImage | null;
  transcriptBtnText?: PortableTextBlock | PortableTextBlock[] | string | null;
  transcript?: PortableTextBlock | PortableTextBlock[] | null;
  urlDesktop: string | null;
  urlMobile: string | null;
};

const Video = ({
  altText,
  className,
  description,
  heading,
  noOverlay = false,
  thumbnailDesktop,
  thumbnailMobile,
  transcript,
  transcriptBtnText,
  urlDesktop,
  urlMobile,
}: Props) => {
  if (!urlDesktop || !thumbnailDesktop) return null;
  const hasTranscript = transcriptBtnText && transcript;

  return (
    <Section className={twMerge(clsx('px-32 py-64 xs:px-0 md:py-96', className))}>
      {heading && (
        <Container className="mb-48 flex flex-col items-center text-center md:mb-64 [&_h2]:mb-16 [&_h2]:text-primary-100 md:[&_h2]:mb-20">
          <CustomPortableText content={heading} />
        </Container>
      )}
      <Container className="flex flex-col items-center text-center">
        <VideoLoaderResponsive
          className="aspect-[0.8] overflow-hidden rounded-12 bg-grey-500 sm:aspect-video md:rounded-16 [&>div>div]:w-full [&>div]:aspect-[0.8] sm:[&>div]:aspect-video"
          mobileAspectRatio={0.8}
          urlVideoMobile={urlMobile ?? urlDesktop}
          urlVideoDesktop={urlDesktop}
          imgThumbnailMobile={thumbnailMobile ?? thumbnailDesktop}
          imgThumbnailDesktop={thumbnailDesktop}
          altText={altText ?? ''}
          noOverlay={noOverlay}
          customIcon={
            <div className="absolute left-1/2 top-1/2 -translate-x-[50%] -translate-y-[50%] rounded-full bg-white p-16 text-grey-500">
              <Play width={24} height={24} />
            </div>
          }
        />
        {description && (
          <div className={twMerge(clsx('mt-16 md:w-2/5 [&_p]:text-md', hasTranscript && 'mb-32'))}>
            <CustomPortableText content={description} />
          </div>
        )}
        {hasTranscript && (
          <Accordion
            title={
              typeof transcriptBtnText === 'string' ? (
                <P>{transcriptBtnText}</P>
              ) : (
                <CustomPortableText content={transcriptBtnText} />
              )
            }
            className={twMerge(
              clsx(
                '[&>button]:mx-auto [&>button]:w-fit [&>button_p]:text-md [&>button_p]:font-medium [&>button_svg]:h-20 [&>button_svg]:w-20',
                !description && 'mt-16',
              ),
            )}
          >
            <div className="max-h-350 overflow-scroll rounded-20 bg-white p-16 text-grey-500">
              <CustomPortableText content={transcript} />
            </div>
          </Accordion>
        )}
      </Container>
    </Section>
  );
};

export default Video;
